import { useState, useMemo } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { searchInstitutions, COMMON_APP_INSTITUTIONS, INSTITUTION_CATEGORIES } from '@/data/commonAppInstitutions';
import { Select } from 'calc-controls';
import { classifyGpaMatch } from '@/utils/gpaMatch';
import { StarIcon as Star } from './ui/Icons';

export function CommonAppBenchmarks() {
  const result = useAppStore((s) => s.result);
  const collegeShortlist = useAppStore((s) => s.collegeShortlist);
  const toggleShortlist = useAppStore((s) => s.toggleShortlist);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');

  const studentGPA = result?.variants.find((v) => v.label === 'Unweighted GPA')?.value ?? null;

  const institutions = useMemo(() => {
    return searchInstitutions(query, category || undefined);
  }, [query, category]);

  return (
    <div className="rounded-lg border border-border bg-surface p-4" data-testid="common-app-benchmarks">
      <h3 className="mb-3 text-sm font-semibold text-[var(--color-text)]">Common App Benchmarks</h3>

      <div className="mb-3 flex gap-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 rounded-md border border-border px-3 py-2 text-sm"
          placeholder="Search institutions..."
          data-testid="input-institution-search"
        />
        <Select
          inline
          value={category}
          onChange={setCategory}
          options={[
            { value: '', label: 'All Categories' },
            ...INSTITUTION_CATEGORIES.map((cat) => ({ value: cat, label: cat })),
          ]}
        />
      </div>

      <p className="mb-2 text-xs text-[var(--color-text-muted)]">
        Showing {institutions.length} of {COMMON_APP_INSTITUTIONS.length} institutions. Your Unweighted GPA:{' '}
        <span className="font-bold text-[var(--color-text)]">
          {studentGPA !== null ? studentGPA.toFixed(2) : '—'}
        </span>
      </p>

      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-xs">
          <caption className="sr-only">Common App institution benchmarks with GPA ranges and match status</caption>
          <thead className="sticky top-0 bg-surface">
            <tr className="border-b border-border text-[var(--color-text-muted)]">
              <th scope="col" className="py-2 text-left">Institution</th>
              <th scope="col" className="py-2 text-right" title="25th percentile Unweighted GPA of enrolled students — 75% scored above this.">25th%</th>
              <th scope="col" className="py-2 text-right" title="Median (50th percentile) Unweighted GPA of enrolled students.">50th%</th>
              <th scope="col" className="py-2 text-right" title="75th percentile Unweighted GPA of enrolled students — only 25% scored above this.">75th%</th>
              <th scope="col" className="py-2 text-right" title="Share of applicants admitted to this institution.">Accept %</th>
              <th scope="col" className="py-2 text-center" title="Where your Unweighted GPA falls relative to this institution&rsquo;s range.">Match</th>
              <th scope="col" className="py-2 text-center" title="Add to your College Shortlist.">★</th>
            </tr>
          </thead>
          <tbody>
            {institutions.slice(0, 30).map((inst) => {
              const match = classifyGpaMatch(studentGPA, inst.gpaRange);
              const starred = collegeShortlist.some((e) => e.key === inst.name);

              const matchColor: Record<string, string> = {
                above: 'text-success',
                middle: 'text-info',
                near: 'text-warning',
                below: 'text-error',
                none: 'text-[var(--color-text-muted)]',
              };

              const matchLabel: Record<string, string> = {
                above: 'Above',
                middle: '~',
                near: '≈',
                below: 'Below',
                none: '—',
              };

              return (
                <tr key={inst.name} className="border-b border-border/50">
                  <td className="py-1.5 font-medium text-[var(--color-text)]">
                    <div>{inst.name}</div>
                    <div className="text-[10px] text-[var(--color-text-muted)]">{inst.category}</div>
                  </td>
                  <td className="py-1.5 text-right">{inst.gpaRange.p25.toFixed(2)}</td>
                  <td className="py-1.5 text-right font-medium">{inst.gpaRange.p50.toFixed(2)}</td>
                  <td className="py-1.5 text-right">{inst.gpaRange.p75.toFixed(2)}</td>
                  <td className="py-1.5 text-right">{(inst.acceptanceRate * 100).toFixed(1)}%</td>
                  <td className={`py-1.5 text-center font-medium ${matchColor[match]}`}>
                    {matchLabel[match]}
                  </td>
                  <td className="py-1.5 text-center">
                    <button
                      type="button"
                      onClick={() =>
                        toggleShortlist({
                          key: inst.name,
                          name: inst.name,
                          source: 'commonapp',
                          range: inst.gpaRange,
                          scale: inst.scale,
                        })
                      }
                      className={`rounded p-1 hover:bg-[var(--color-bg-tertiary)] ${
                        starred ? 'text-warning' : 'text-[var(--color-text-muted)]'
                      }`}
                      aria-label={starred ? `Remove ${inst.name} from shortlist` : `Add ${inst.name} to shortlist`}
                      aria-pressed={starred}
                      data-testid={`shortlist-toggle-${inst.name}`}
                    >
                      <Star
                        className="h-3.5 w-3.5"
                        fill={starred ? 'currentColor' : 'none'}
                        aria-hidden="true"
                      />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {institutions.length > 30 && (
          <p className="py-2 text-center text-xs text-[var(--color-text-muted)]">
            …and {institutions.length - 30} more. Narrow your search to see them.
          </p>
        )}
      </div>
    </div>
  );
}
